import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import reportSpanish from './locales/report.es.json';

const resources = {
  en: {
    report: Object.fromEntries(Object.keys(reportSpanish).map(key => [key, key])),
    translation: {
      header: {
        secureViewer: 'Secure report viewer',
        secureViewerShort: 'Secure viewer',
        clinicalReport: 'Clinical Report',
        secureHealthReport: 'Secure health report',
        viewReport: 'View report',
        reportShort: 'Report',
        downloadReport: 'Download report',
        downloadShort: 'Download',
        reportLinkExpires: 'Report link Expires {{date}}',
        dateCollected: 'Date collected: {{date}}',
        reviewSoon: 'Review soon',
        disclaimer: 'This page shows the parent-submitted clinical summary, follow-up context, and attached videos together for a faster clinical read.',
        dob: 'DOB {{date}}',
        insurance: 'Insurance: {{status}}',
        ageYearsMonths: '{{years}}y {{months}}m',
        ageYears: '{{years}}y',
        ageMonths: '{{months}}m',
        views_one: '{{count}} view',
        views_other: '{{count}} views',
        languageLabel: 'Switch web interface to Spanish',
        gender: {
          female: 'Female',
          male: 'Male',
          nonBinary: 'Non-binary',
          other: 'Other',
          notSpecified: 'Gender not specified',
        },
        insuranceStatus: {
          privateEmployer: 'Private or employer-based insurance',
          medicaidChip: 'Medicaid / CHIP',
          uninsured: 'Uninsured',
          limited: 'Limited coverage',
          other: 'Other insurance',
          unknown: 'Insurance unknown',
        },
      },
      sectionNav: {
        label: 'Report sections',
        jumpTo: 'Jump to',
        overview: 'Overview',
        caregiverDetails: 'Caregiver details',
        providerNote: 'Provider note',
        providerResources: 'Provider resources',
        support: 'Support',
        mobileApp: 'Mobile app',
      },
      healthHistory: {
        eyebrow: 'Health History',
        title: 'Health History',
        subtitle: 'Parent-reported background conditions, exposures, allergies, immunizations, birth history, and medications the child takes regularly.',
        empty: 'No health history was included in this report.',
        groups: {
          respiratory: 'Respiratory risk history',
          allergies: 'Allergies',
          birth: 'Birth and NICU history',
          immunizations: 'Immunizations',
          exposure: 'Smoke and environmental exposure',
          medications: 'Medications the child takes regularly',
          other: 'Other history',
        },
      },
      common: {
        copied: 'Copied',
        cancel: 'Cancel',
        saving: 'Saving',
        submit: 'Submit',
        notReported: 'Not reported',
      },
      workflow: {
        eyebrow: 'Clinical Workflow',
        title: 'How to Use This Page',
        item1: 'Start with level of concern and main concern, then review the structured symptom, exposure, activity, and follow-up details.',
        item2: 'Use the video sequence to compare the initial upload with follow-up progression.',
      },
      parentInfo: {
        eyebrow: 'Caregiver Details',
        title: 'Parent Contact',
        researchId: 'Research ID',
        name: 'Name',
        email: 'Email',
        phone: 'Phone',
        location: 'Location',
        relationship: 'Relationship to Child',
        communityType: 'Community type',
        copyEmailTitle: 'Click to copy email',
        copyEmailAria: 'Copy email to clipboard',
      },
      resources: {
        eyebrow: 'Provider Resources',
        title: 'Reference Links',
        insuranceTitle: 'Insurance Reimbursement Information',
        insuranceDetail: 'Coverage and billing guidance for clinicians using MamaBear.',
        medicalTitle: 'Medical Necessity Language',
        medicalDetail: 'Reference copy for documentation and clinical justification.',
        workflowTitle: 'Workflow Information',
        workflowDetail: 'Operational guidance for integrating report review into follow-up workflows.',
        externalTitle: 'Opens external resource',
      },
      support: {
        eyebrow: 'Support',
        title: 'Questions About This Report?',
        subtitle: 'Copy the support email if you need help with access, report interpretation, or workflow setup.',
        copyTitle: 'Click to copy email',
        copyAria: 'Copy support email to clipboard',
      },
      mobileApp: {
        eyebrow: 'Mobile App',
        title: 'Install MamaBear Health on your phone',
        subtitle: 'Families can keep collecting symptom reports and follow-up videos directly from the app.',
      },
      clinicalFindings: {
        eyebrow: 'Provider note',
        title: 'Clinical note',
        subtitle: 'Provider-authored note connected to this report.',
        editTitle: 'Edit clinical findings',
        deleteTitle: 'Delete clinical findings',
        notes: 'Notes',
        placeholder: 'Enter clinical findings for the parent-visible report.',
        required: 'Clinical findings are required.',
        tooLong: 'Clinical findings must be 400 characters or fewer.',
        saved: 'Clinical findings saved.',
        deleted: 'Clinical findings deleted.',
        saveFailed: 'We could not save the clinical findings. Please try again.',
        deleteFailed: 'We could not delete the clinical findings. Please try again.',
        requestFollowUp: 'Ask parent to collect another eCheck',
        requestingFollowUp: 'Sending...',
        requestFollowUpTitle: 'Text the parent to collect another eCheck',
        followUpSent: 'eCheck request text sent.',
        followUpFailed: 'We could not text the parent. Please try again.',
        remaining: '{{count}} characters remaining',
        empty: 'No clinical findings have been added yet.',
        lastUpdated: 'Last updated {{date}}',
      },
      myChart: {
        title: 'Send to EHR',
        subtitle: 'Copy a secure message for your EHR',
        preparedTitle: 'Prepared EHR message',
        messageReview: 'Please review this MamaBear Health report.',
        reportLink: 'Report link:',
        accessCode: 'Access code:',
        sharedBy: 'This report was shared by the patient/guardian through MamaBear Health.',
        missingCode: 'Include the report access code from your email when you send this link.',
        guidance: 'MamaBear can prepare the message, but you will choose the recipient and click Send inside your EHR.',
        linkUnavailable: 'Report link is unavailable.',
        copyFailed: 'We could not copy the message. Please copy it manually.',
        clipboardBlocked: 'Clipboard access was blocked. Copy the message manually.',
        copiedOpened: 'Message copied, EHR opened',
        copied: 'Message copied',
        copyOpen: 'Copy message & open EHR',
        copy: 'Copy message',
        copiedNotice: 'Your message has been copied.',
        copiedBlockedNotice: 'Your message has been copied. Open your EHR to send it.',
        step1: 'Choose Messages, Inbox, or Ask a Question.',
        step2: 'Select your provider or care team.',
        step3: 'Paste the copied MamaBear message.',
        step4: 'Click Send.',
        manualTitle: 'Your browser blocked automatic clipboard access.',
        manualBody: 'Select and copy this message, then open your EHR and paste it into a new message.',
        textareaAria: 'Prepared EHR message to copy',
        selectMessage: 'Select message',
        openMyChart: 'Open EHR',
        footer: 'The report remains protected by the secure link and access code.',
      },
      clinicalSummary: {
        title: 'eCheck Summary',
        subtitle: 'Review the reported symptoms, treatment context, and follow-up progression directly on the page.',
        visitSnapshot: 'Visit Snapshot',
        reportedSymptoms: 'Reported Symptoms',
        symptomsTitle: 'Symptoms observed by the parents',
        thisChild: 'this child',
        noSymptoms: 'No symptom details were included in this report.',
        careEyebrow: 'Care Context',
        careTitle: 'eCheck details',
        noCare: 'No additional visit context was shared.',
        followUpReview: 'Follow-up Review',
        followUpEyebrow: 'Follow-up evaluations',
        followUpTitle: 'Follow-up progression',
        followUpSubtitle: 'Track how symptoms changed after the first recording',
        followUpRecordings_one: '{{count}} follow-up recording available',
        followUpRecordings_other: '{{count}} follow-up recordings available',
        requestFollowUp: 'Ask parent to collect another eCheck',
        requestingFollowUp: 'Sending...',
        requestFollowUpTitle: 'Text the parent to collect another eCheck',
        followUpRequestSent: 'eCheck request text sent.',
        followUpRequestFailed: 'We could not text the parent. Please try again.',
        noFollowUpProgression: 'No follow-up progression has been collected yet.',
        resolved: '{{count}} resolved',
        improving: '{{count}} improving',
        worsening: '{{count}} worsening',
        symptom: 'Symptom',
        initial: 'Initial',
        followUp1: 'Follow-up 1',
        followUp2: 'Follow-up 2',
        noFollowUp: 'No follow-up',
        attachedNoStructured: 'Follow-up recordings are attached, but structured symptom changes were not captured in the report data.',
        activeSymptoms: 'Active symptoms reported',
        noActiveSymptoms: 'No active symptoms reported',
        medicationGiven: 'Medication given before eCheck{{summary}}',
        noMedicationGiven: 'No medication given before eCheck',
        childAlreadySeen: 'Child was already seen: {{location}}',
        providerRequested: 'Doctor asked for a follow-up',
        followUpRequested: 'Follow-up requested',
        recordingsAvailable: 'Follow-up recordings available',
        labels: {
          childSeenIn: 'Where child was seen',
          symptomDuration: 'Symptom duration',
          repetitivePattern: 'Repetitive pattern',
          sleepQuality: 'Sleep quality',
          activityLevel: 'Activity level',
          hydration: 'Hydration',
          urination: 'Urination',
          dehydrationSigns: 'Dehydration signs',
          stools: 'Stools',
          normal: 'Normal',
          yes: 'Yes',
          no: 'No',
          noneReported: 'None reported',
        },
        symptoms: {
          fever: 'Fever',
          cough: 'Cough',
          rapidBreathing: 'Rapid breathing',
          breathingDifficulties: 'Breathing difficulties',
          noisyBreathing: 'Noisy breathing',
          chestRetractions: 'Chest retractions',
          nasalCongestion: 'Nasal congestion',
          vomitingDiarrhea: 'Vomiting/diarrhea',
          rapidHeartRate: 'Rapid heart rate',
          soreThroat: 'Sore throat',
          earPain: 'Ear pain',
        },
      },
      concernSummary: {
        eyebrow: 'Provider Priority',
        title: 'Level of concern and main concern',
        subtitle: 'Provider-facing fields for EMR documentation and reimbursement review.',
        levelOfConcern: 'Level of concern',
        mainConcern: 'Main concern',
        repetitivePattern: 'Repetitive pattern',
        additionalComments: 'Additional comments',
      },
      checkInView: {
        levelOfConcern: 'Level of concern',
        levelAria: 'Level of concern {{value}} of 10',
        levelNotRecordedAria: 'Level of concern not recorded',
        notRecorded: 'Not recorded',
        parentConcern: 'Parent concern',
        noParentConcern: 'No parent concern recorded.',
        comparedTo: 'Compared to {{label}}{{date}}',
        previousCheckIn: 'previous check-in',
        objective: 'O — Objective',
        videoEvidence: 'Video evidence',
        noVideo: 'No video',
        noVideoForCheckIn: 'No video is available for this check-in.',
        temperature: 'Temperature',
        activityLevel: 'Activity level',
        activityOnset: 'Activity onset',
        fluids: 'Fluids',
        urination: 'Urination',
        dehydrationSigns: 'Dehydration signs',
        stools: 'Stools',
        diaper: 'Diaper',
        sleep: 'Sleep',
        hydrationLevel: 'Hydration level',
        checkInContext: 'Check-in context',
        activeSymptoms: 'Active symptoms',
        seenByProvider: 'Seen by provider',
        whereChildWasSeen: 'Where child was seen',
        symptomsAtFollowUp: 'Reason for follow-up',
        diagnosis: 'Diagnosis',
        treatments: 'Treatments',
        providerRequestedFollowUp: 'Doctor asked for a follow-up',
        howDoing: 'How are they doing?',
        providerRequestedNoteWithLocation: 'The child was seen at {{location}}, and the clinician requested this eCheck.',
        providerRequestedNote: 'A clinician requested this eCheck; the prior care setting was not reported.',
        symptomsBeforeECheck: 'Symptoms reported before this eCheck: {{symptoms}}.',
        symptomsBeforeECheckMissing: 'Symptoms before this eCheck were not reported.',
        symptomChangesAtECheck: 'Changes reported at this eCheck: {{changes}}.',
        sickContacts: 'Sick contacts',
        exposures: 'Exposures',
        activityWhenSymptomsNoticed: 'Activity when symptoms were first noticed',
        medications: 'Medications',
        noMedications: 'No medications reported.',
        yes: 'Yes',
        no: 'No',
        noneReported: 'None reported',
        medicationAlreadyGiven: 'Medication already given',
        noMedicationGiven: 'No medication given',
        medicationStatusAlreadyGiven: 'Medications already given — symptoms reflect post-medication state',
        medicationStatusWillGive: 'Parent plans to give medications — symptoms may reflect pre-medication state',
        alertModerateEd: 'Multiple moderate symptoms. Oxygen, nebulizer, steroids given at ED. Elevated mAPI risk. Review video.',
        severity: 'Severity',
        severityLabels: {
          mild: 'Mild',
          moderate: 'Moderate',
          severe: 'Severe',
          trace: 'Trace',
          none: 'None',
          noSymptoms: 'No symptoms',
          resolved: 'Resolved',
          muchBetter: 'Much better',
          littleBetter: 'A little better',
          gettingWorse: 'Getting worse',
          noChange: 'No change',
          unknown: 'Unknown',
        },
      },
      videoSection: {
        attachedMedia: 'Attached Media',
        videoTimeline: 'Video Timeline',
        subtitle: 'Compare the initial upload with follow-up recordings to assess trend, response, and escalation need.',
        videoSequence: 'Video Sequence',
        initialUpload: 'Initial upload',
        eCheckNumber: 'eCheck {{number}}',
        followUp1: 'Follow-up 1',
        followUp2: 'Follow-up 2',
        followUpNumber: 'Follow-up {{number}}',
        followUpRequestBadge: 'Follow-up needed',
        requestFollowUp: 'Ask parent to collect another eCheck',
        requestingFollowUp: 'Sending...',
        requestFollowUpTitle: 'Text the parent to collect another eCheck',
        followUpRequestSent: 'eCheck request text sent.',
        followUpRequestFailed: 'We could not text the parent. Please try again.',
        timeUnavailable: 'Time unavailable',
        baselineDetail: 'Baseline upload from the first report submission.',
        followUpDetail: 'Follow-up capture collected after the initial report for progression review.',
        duration: 'Duration: {{duration}}',
        openVideo: 'Open video in a new tab',
        unsupported: 'This video format may not be supported by your browser. If recorded on iPhone, try Safari or open in a new tab.',
        unableToPlay: 'Unable to play this video in your browser.',
        collapse: 'Collapse',
        expand: 'Expand in place',
        collapseAria: 'Collapse video',
        expandAria: 'Expand video in place',
        fill: 'Fill (zoom to fill box)',
        fit: 'Fit (show entire video)',
        fillAria: 'Fill video',
        fitAria: 'Fit video',
        fullscreen: 'Fullscreen',
        fullscreenAria: 'Enter fullscreen',
      },
    },
  },
  es: {
    report: reportSpanish,
    translation: {
      header: {
        secureViewer: 'Visor seguro del informe',
        secureViewerShort: 'Visor seguro',
        clinicalReport: 'Informe clínico',
        secureHealthReport: 'Informe de salud seguro',
        viewReport: 'Ver informe',
        reportShort: 'Informe',
        downloadReport: 'Descargar informe',
        downloadShort: 'Descargar',
        reportLinkExpires: 'El enlace del informe vence el {{date}}',
        dateCollected: 'Fecha de recolección: {{date}}',
        reviewSoon: 'Revisar pronto',
        disclaimer: 'Esta página muestra el resumen clínico enviado por el padre o tutor, el contexto de seguimiento y los videos adjuntos para una revisión clínica más rápida.',
        dob: 'Fecha de nacimiento {{date}}',
        insurance: 'Seguro: {{status}}',
        ageYearsMonths: '{{years}} años {{months}} meses',
        ageYears: '{{years}} años',
        ageMonths: '{{months}} meses',
        views_one: '{{count}} vista',
        views_other: '{{count}} vistas',
        languageLabel: 'Cambiar la interfaz web al inglés',
        gender: {
          female: 'Femenino',
          male: 'Masculino',
          nonBinary: 'No binario',
          other: 'Otro',
          notSpecified: 'Género no especificado',
        },
        insuranceStatus: {
          privateEmployer: 'Seguro privado o por empleador',
          medicaidChip: 'Medicaid / CHIP',
          uninsured: 'Sin seguro',
          limited: 'Cobertura limitada',
          other: 'Otro seguro',
          unknown: 'Seguro desconocido',
        },
      },
      sectionNav: {
        label: 'Secciones del informe',
        jumpTo: 'Ir a',
        overview: 'Resumen',
        caregiverDetails: 'Datos del cuidador',
        providerNote: 'Nota del proveedor',
        providerResources: 'Recursos para proveedores',
        support: 'Soporte',
        mobileApp: 'Aplicación móvil',
      },
      healthHistory: {
        eyebrow: 'Historial de salud',
        title: 'Historial de salud',
        subtitle: 'Condiciones de base, exposiciones, alergias, vacunas, antecedentes de nacimiento y medicamentos que el niño toma regularmente reportados por el padre o tutor.',
        empty: 'No se incluyó historial de salud en este informe.',
        groups: {
          respiratory: 'Antecedentes de riesgo respiratorio',
          allergies: 'Alergias',
          birth: 'Nacimiento y NICU',
          immunizations: 'Vacunas',
          exposure: 'Humo y exposición ambiental',
          medications: 'Medicamentos que el niño toma regularmente',
          other: 'Otros antecedentes',
        },
      },
      common: {
        copied: 'Copiado',
        cancel: 'Cancelar',
        saving: 'Guardando',
        submit: 'Enviar',
        notReported: 'No reportado',
      },
      workflow: {
        eyebrow: 'Flujo clínico',
        title: 'Cómo usar esta página',
        item1: 'Comience con el nivel de preocupación y la preocupación principal; luego revise los detalles estructurados de síntomas, exposición, actividad y seguimiento.',
        item2: 'Use la secuencia de videos para comparar la carga inicial con la evolución de seguimiento.',
      },
      parentInfo: {
        eyebrow: 'Datos del cuidador',
        title: 'Contacto del padre o tutor',
        researchId: 'ID de investigación',
        name: 'Nombre',
        email: 'Correo electrónico',
        phone: 'Teléfono',
        location: 'Ubicación',
        relationship: 'Relación con el niño',
        communityType: 'Tipo de comunidad',
        copyEmailTitle: 'Haga clic para copiar el correo',
        copyEmailAria: 'Copiar correo al portapapeles',
      },
      resources: {
        eyebrow: 'Recursos para proveedores',
        title: 'Enlaces de referencia',
        insuranceTitle: 'Información de reembolso del seguro',
        insuranceDetail: 'Guía de cobertura y facturación para clínicos que usan MamaBear.',
        medicalTitle: 'Lenguaje de necesidad médica',
        medicalDetail: 'Texto de referencia para documentación y justificación clínica.',
        workflowTitle: 'Información del flujo de trabajo',
        workflowDetail: 'Guía operativa para integrar la revisión de informes en los flujos de seguimiento.',
        externalTitle: 'Abre un recurso externo',
      },
      support: {
        eyebrow: 'Soporte',
        title: '¿Preguntas sobre este informe?',
        subtitle: 'Copie el correo de soporte si necesita ayuda con el acceso, la interpretación del informe o la configuración del flujo de trabajo.',
        copyTitle: 'Haga clic para copiar el correo',
        copyAria: 'Copiar correo de soporte al portapapeles',
      },
      mobileApp: {
        eyebrow: 'Aplicación móvil',
        title: 'Instala MamaBear Health en tu teléfono',
        subtitle: 'Las familias pueden seguir recopilando informes de síntomas y videos de seguimiento directamente desde la aplicación.',
      },
      clinicalFindings: {
        eyebrow: 'Nota del proveedor',
        title: 'Nota clínica',
        subtitle: 'Nota escrita por el proveedor conectada a este informe.',
        editTitle: 'Editar hallazgos clínicos',
        deleteTitle: 'Eliminar hallazgos clínicos',
        notes: 'Notas',
        placeholder: 'Ingrese hallazgos clínicos para el informe visible para los padres.',
        required: 'Los hallazgos clínicos son obligatorios.',
        tooLong: 'Los hallazgos clínicos deben tener 400 caracteres o menos.',
        saved: 'Hallazgos clínicos guardados.',
        deleted: 'Hallazgos clínicos eliminados.',
        saveFailed: 'No se pudieron guardar los hallazgos clínicos. Inténtelo de nuevo.',
        deleteFailed: 'No se pudieron eliminar los hallazgos clínicos. Inténtelo de nuevo.',
        requestFollowUp: 'Pedir al padre otro eCheck',
        requestingFollowUp: 'Enviando...',
        requestFollowUpTitle: 'Enviar texto al padre para pedir otro eCheck',
        followUpSent: 'Texto de solicitud de eCheck enviado.',
        followUpFailed: 'No se pudo enviar el mensaje de texto al padre. Inténtelo de nuevo.',
        remaining: 'Quedan {{count}} caracteres',
        empty: 'Aún no se han agregado hallazgos clínicos.',
        lastUpdated: 'Última actualización {{date}}',
      },
      myChart: {
        title: 'Enviar al EHR',
        subtitle: 'Copie un mensaje seguro para su EHR',
        preparedTitle: 'Mensaje preparado para EHR',
        messageReview: 'Por favor revise este informe de MamaBear Health.',
        reportLink: 'Enlace del informe:',
        accessCode: 'Código de acceso:',
        sharedBy: 'Este informe fue compartido por el paciente/tutor a través de MamaBear Health.',
        missingCode: 'Incluya el código de acceso del informe de su correo electrónico cuando envíe este enlace.',
        guidance: 'MamaBear puede preparar el mensaje, pero usted elegirá el destinatario y hará clic en Enviar dentro de su EHR.',
        linkUnavailable: 'El enlace del informe no está disponible.',
        copyFailed: 'No se pudo copiar el mensaje. Cópielo manualmente.',
        clipboardBlocked: 'El acceso al portapapeles fue bloqueado. Copie el mensaje manualmente.',
        copiedOpened: 'Mensaje copiado, EHR abierto',
        copied: 'Mensaje copiado',
        copyOpen: 'Copiar mensaje y abrir EHR',
        copy: 'Copiar mensaje',
        copiedNotice: 'Su mensaje ha sido copiado.',
        copiedBlockedNotice: 'Su mensaje ha sido copiado. Abra su EHR para enviarlo.',
        step1: 'Elija Mensajes, Bandeja de entrada o Hacer una pregunta.',
        step2: 'Seleccione su proveedor o equipo de atención.',
        step3: 'Pegue el mensaje copiado de MamaBear.',
        step4: 'Haga clic en Enviar.',
        manualTitle: 'Su navegador bloqueó el acceso automático al portapapeles.',
        manualBody: 'Seleccione y copie este mensaje; luego abra su EHR y péguelo en un mensaje nuevo.',
        textareaAria: 'Mensaje preparado para EHR para copiar',
        selectMessage: 'Seleccionar mensaje',
        openMyChart: 'Abrir EHR',
        footer: 'El informe permanece protegido por el enlace seguro y el código de acceso.',
      },
      clinicalSummary: {
        title: 'Resumen del eCheck',
        subtitle: 'Revise los síntomas reportados, el contexto del tratamiento y la evolución de seguimiento directamente en la página.',
        visitSnapshot: 'Resumen de la visita',
        reportedSymptoms: 'Síntomas reportados',
        symptomsTitle: 'Síntomas observados por los padres',
        thisChild: 'este niño',
        noSymptoms: 'No se incluyeron detalles de síntomas en este informe.',
        careEyebrow: 'Contexto de atención',
        careTitle: 'Detalles del eCheck',
        noCare: 'No se compartió contexto adicional de la visita.',
        followUpReview: 'Revisión de seguimiento',
        followUpEyebrow: 'Evaluaciones de seguimiento',
        followUpTitle: 'Progresión de seguimiento',
        followUpSubtitle: 'Revise cómo cambiaron los síntomas después de la primera grabación',
        followUpRecordings_one: '{{count}} grabación de seguimiento disponible',
        followUpRecordings_other: '{{count}} grabaciones de seguimiento disponibles',
        requestFollowUp: 'Pedir otro eCheck al padre',
        requestingFollowUp: 'Enviando...',
        requestFollowUpTitle: 'Enviar texto al padre para pedir otro eCheck',
        followUpRequestSent: 'Texto de solicitud de eCheck enviado.',
        followUpRequestFailed: 'No se pudo enviar el mensaje de texto al padre. Inténtelo de nuevo.',
        noFollowUpProgression: 'Aún no se ha recopilado progreso de seguimiento.',
        resolved: '{{count}} resueltos',
        improving: '{{count}} mejorando',
        worsening: '{{count}} empeorando',
        symptom: 'Síntoma',
        initial: 'Inicial',
        followUp1: 'Seguimiento 1',
        followUp2: 'Seguimiento 2',
        noFollowUp: 'Sin seguimiento',
        attachedNoStructured: 'Hay grabaciones de seguimiento adjuntas, pero los cambios estructurados de síntomas no se capturaron en los datos del informe.',
        activeSymptoms: 'Síntomas activos reportados',
        noActiveSymptoms: 'No se reportaron síntomas activos',
        medicationGiven: 'Medicamento administrado antes del eCheck{{summary}}',
        noMedicationGiven: 'No se administró medicamento antes del eCheck',
        childAlreadySeen: 'El menor ya fue atendido: {{location}}',
        providerRequested: 'El médico pidió seguimiento',
        followUpRequested: 'Seguimiento solicitado',
        recordingsAvailable: 'Grabaciones de seguimiento disponibles',
        labels: {
          childSeenIn: 'Dónde atendieron al niño',
          symptomDuration: 'Duración de los síntomas',
          repetitivePattern: 'Patrón repetitivo',
          sleepQuality: 'Calidad del sueño',
          activityLevel: 'Nivel de actividad',
          hydration: 'Hidratación',
          urination: 'Orina',
          dehydrationSigns: 'Signos de deshidratación',
          stools: 'Heces',
          normal: 'Normal',
          yes: 'Sí',
          no: 'No',
          noneReported: 'Ninguno reportado',
        },
        symptoms: {
          fever: 'Fiebre',
          cough: 'Tos',
          rapidBreathing: 'Respiración rápida',
          breathingDifficulties: 'Dificultades para respirar',
          noisyBreathing: 'Respiración ruidosa',
          chestRetractions: 'Retracciones del pecho',
          nasalCongestion: 'Congestión nasal',
          vomitingDiarrhea: 'Vómitos/diarrea',
          rapidHeartRate: 'Frecuencia cardíaca rápida',
          soreThroat: 'Dolor de garganta',
          earPain: 'Dolor de oído',
        },
      },
      concernSummary: {
        eyebrow: 'Prioridad para el proveedor',
        title: 'Nivel de preocupación y preocupación principal',
        subtitle: 'Campos orientados al proveedor para documentación en EMR y revisión de reembolso.',
        levelOfConcern: 'Nivel de preocupación',
        mainConcern: 'Preocupación principal',
        repetitivePattern: 'Patrón repetitivo',
        additionalComments: 'Comentarios adicionales',
      },
      checkInView: {
        levelOfConcern: 'Nivel de preocupación',
        levelAria: 'Nivel de preocupación {{value}} de 10',
        levelNotRecordedAria: 'Nivel de preocupación no registrado',
        notRecorded: 'No registrado',
        parentConcern: 'Preocupación del padre o tutor',
        noParentConcern: 'No se registró preocupación del padre o tutor.',
        comparedTo: 'Comparado con {{label}}{{date}}',
        previousCheckIn: 'chequeo anterior',
        objective: 'O — Objetivo',
        videoEvidence: 'Evidencia en video',
        noVideo: 'Sin video',
        noVideoForCheckIn: 'No hay video disponible para este check-in.',
        temperature: 'Temperatura',
        activityLevel: 'Nivel de actividad',
        activityOnset: 'Inicio de actividad',
        fluids: 'Líquidos',
        urination: 'Orina',
        dehydrationSigns: 'Signos de deshidratación',
        stools: 'Heces',
        diaper: 'Pañal',
        sleep: 'Sueño',
        hydrationLevel: 'Nivel de hidratación',
        checkInContext: 'Contexto del check-in',
        activeSymptoms: 'Síntomas activos',
        seenByProvider: 'Atendido por proveedor',
        whereChildWasSeen: 'Dónde atendieron al niño',
        symptomsAtFollowUp: 'Motivo del seguimiento',
        diagnosis: 'Diagnóstico',
        treatments: 'Tratamientos',
        providerRequestedFollowUp: 'El médico pidió seguimiento',
        howDoing: '¿Cómo está?',
        providerRequestedNoteWithLocation: 'El menor fue atendido en {{location}} y el profesional clínico solicitó este eCheck.',
        providerRequestedNote: 'Un profesional clínico solicitó este eCheck; no se informó el lugar de atención anterior.',
        symptomsBeforeECheck: 'Síntomas informados antes de este eCheck: {{symptoms}}.',
        symptomsBeforeECheckMissing: 'No se informaron los síntomas anteriores a este eCheck.',
        symptomChangesAtECheck: 'Cambios informados en este eCheck: {{changes}}.',
        sickContacts: 'Contactos enfermos',
        exposures: 'Exposiciones',
        activityWhenSymptomsNoticed: 'Actividad cuando se notaron los síntomas por primera vez',
        medications: 'Medicamentos',
        noMedications: 'No se reportaron medicamentos.',
        yes: 'Sí',
        no: 'No',
        noneReported: 'Ninguno reportado',
        medicationAlreadyGiven: 'Medicamento ya administrado',
        noMedicationGiven: 'No se administró medicamento',
        medicationStatusAlreadyGiven: 'Medicamentos ya administrados — los síntomas reflejan el estado posterior al medicamento',
        medicationStatusWillGive: 'El padre o tutor planea administrar medicamentos — los síntomas pueden reflejar el estado previo al medicamento',
        alertModerateEd: 'Múltiples síntomas moderados. Oxígeno, nebulizador y esteroides administrados en urgencias. Riesgo mAPI elevado. Revise el video.',
        severity: 'Gravedad',
        severityLabels: {
          mild: 'Leve',
          moderate: 'Moderado',
          severe: 'Grave',
          trace: 'Mínimo',
          none: 'Ninguno',
          noSymptoms: 'Sin síntomas',
          resolved: 'Resuelto',
          muchBetter: 'Mucho mejor',
          littleBetter: 'Un poco mejor',
          gettingWorse: 'Empeorando',
          noChange: 'Sin cambio',
          unknown: 'Desconocido',
        },
      },
      videoSection: {
        attachedMedia: 'Medios adjuntos',
        videoTimeline: 'Línea de tiempo de videos',
        subtitle: 'Compare la carga inicial con las grabaciones de seguimiento para evaluar tendencia, respuesta y necesidad de escalamiento.',
        videoSequence: 'Secuencia de video',
        initialUpload: 'Carga inicial',
        eCheckNumber: 'eCheck {{number}}',
        followUp1: 'Seguimiento 1',
        followUp2: 'Seguimiento 2',
        followUpNumber: 'Seguimiento {{number}}',
        followUpRequestBadge: 'Seguimiento necesario',
        requestFollowUp: 'Pedir otro eCheck al padre',
        requestingFollowUp: 'Enviando...',
        requestFollowUpTitle: 'Enviar texto al padre para pedir otro eCheck',
        followUpRequestSent: 'Texto de solicitud de eCheck enviado.',
        followUpRequestFailed: 'No se pudo enviar el mensaje de texto al padre. Inténtelo de nuevo.',
        timeUnavailable: 'Hora no disponible',
        baselineDetail: 'Carga de referencia del primer envío del informe.',
        followUpDetail: 'Captura de seguimiento recopilada después del informe inicial para revisar la evolución.',
        duration: 'Duración: {{duration}}',
        openVideo: 'Abrir video en una pestaña nueva',
        unsupported: 'Es posible que este formato de video no sea compatible con su navegador. Si fue grabado en iPhone, pruebe Safari o ábralo en una pestaña nueva.',
        unableToPlay: 'No se puede reproducir este video en su navegador.',
        collapse: 'Contraer',
        expand: 'Expandir aquí',
        collapseAria: 'Contraer video',
        expandAria: 'Expandir video aquí',
        fill: 'Llenar (ampliar para ocupar el cuadro)',
        fit: 'Ajustar (mostrar todo el video)',
        fillAria: 'Llenar video',
        fitAria: 'Ajustar video',
        fullscreen: 'Pantalla completa',
        fullscreenAria: 'Entrar en pantalla completa',
      },
    },
  },
};

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: localStorage.getItem('reportLanguage') || 'en',
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
  });

export default i18n;
